import { useLocalStorage } from '@vueuse/core'

export const useDraft = <T = RecordSchema, I = unknown>(STORAGE_KEY: string, DEBOUNCE_MS = 700) => {
	const storage = useLocalStorage<T | string>(STORAGE_KEY, null, { deep: true, mergeDefaults: true })
	const loadDraft = computed(() => decryptString<T & { lastSavedAt: number }>(storage.value as string) || null)
	const getItem = computed(() => {
		const item = storage.value as string
		if (isValidJSON(item)) {
			return JSON.parse(item) as T
		}
		return item as T
	})
	const getReferrer = computed(() => getLocalStorage('referrer') ?? '')
	const getChannelSource = computed(() => getLocalStorage('channel_source') ?? '')
	const getCampaign = computed(() => getLocalStorage('campaign_code') ?? '')

	const saveDraft = (
		draft: RecordSchema,
		options: boolean | { ignoreLastSavedAt?: boolean; encrypt?: boolean } = false
	) => {
		return resolve(
			() => {
				if (typeof options === 'boolean') {
					if (!options) {
						draft.lastSavedAt = Date.now()
					}
				} else {
					if (!options.ignoreLastSavedAt) {
						draft.lastSavedAt = Date.now()
					}
					if (options.encrypt) {
						storage.value = encryptData(JSON.stringify(draft))
						return storage.value as T
					}
				}
				storage.value = JSON.stringify(draft)
				return storage.value as T
			},
			false,
			100
		)
	}
	const updateDraft = (draft: Partial<T>) => {
		return resolve(
			() => {
				if (typeof loadDraft.value === 'object' && Object.keys(draft).length > 0) {
					storage.value = JSON.stringify({ ...loadDraft.value, ...draft })
				} else {
					storage.value = JSON.stringify({ ...draft, lastSavedAt: Date.now() })
				}
				return storage.value as T
			},
			false,
			100
		)
	}
	const storeItem = (item: I, encrypt = false) => {
		if (encrypt) {
			storage.value = JSON.stringify(encryptData(item))
		} else {
			storage.value = JSON.stringify(item)
		}
	}

	const clearDraft = (clearAll = false) => {
		storage.value = null
		if (clearAll) {
			// Clear all items from localStorage
			localStorage.clear()
		}
	}

	let saveTimeout: NodeJS.Timeout | null = null

	const scheduleSave = (updateFn: (draft: RecordSchema) => RecordSchema) => {
		if (saveTimeout) clearTimeout(saveTimeout)
		saveTimeout = setTimeout(() => {
			const existing = loadDraft.value
			if (!existing) return
			const updated = updateFn(existing)
			saveDraft(updated)
		}, DEBOUNCE_MS)
	}

	return {
		getReferrer,
		getChannelSource,
		getCampaign,
		updateDraft,
		loadDraft,
		saveDraft,
		clearDraft,
		scheduleSave,
		storeItem,
		getItem
	}
}
